// src/components/shared/forms/LocationRow.tsx
//
// Country → Region → City picker sourced from the Locations module in the
// database (Settings → Locations), not from an offline dataset. Anything that
// can be selected here is a real row in locationCountries / locationRegions /
// locationCities, so addresses always line up with the rest of the platform.
//
// Drop-in for the old CSCRow: it emits the same { countryCode, country, state,
// city } shape, so consuming forms and API payloads stay unchanged.

import React from "react";
import SelectRS, { type SingleValue } from "react-select";

import Field from "@/components/shared/ui/Field";
import { useCountries, useRegions, useCities } from "@/api/features/locations";

export type LocationFormValue = {
  countryIso2?: string;
  /** Kept as `countryCode` too, for drop-in compatibility with CSCRow. */
  countryCode?: string;
  country?: string;
  /** Region/state NAME (the column the forms already store). */
  state?: string;
  city?: string;
};

type Opt = { value: string; label: string };

/** The list endpoints cap `limit` at 100. */
const LIMIT = 100;

const selectStyles = {
  control: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: 38,
    borderRadius: 8,
  }),
  menu: (base: Record<string, unknown>) => ({ ...base, zIndex: 30 }),
};

export default function LocationRow({
  value,
  onChange,
  instancePrefix = "loc",
  showRegion = true,
}: {
  value: LocationFormValue;
  onChange: (v: LocationFormValue) => void;
  instancePrefix?: string;
  /** Hide the middle select where a region adds nothing. */
  showRegion?: boolean;
}) {
  /* ----------------------------- Countries ----------------------------- */

  const { data: countriesRes, isLoading: countriesLoading } = useCountries({
    limit: LIMIT,
  });
  const countries = React.useMemo(
    () => countriesRes?.items ?? [],
    [countriesRes]
  );

  // Forms may hold only the country NAME (that is what CSCRow stored), so the
  // ISO code is recovered from the name when it is missing.
  const selectedCountry = React.useMemo(() => {
    const iso = String(value.countryIso2 ?? value.countryCode ?? "")
      .trim()
      .toUpperCase();
    if (iso) return countries.find((c) => c.iso2 === iso) ?? null;

    const name = String(value.country ?? "").trim().toLowerCase();
    if (!name) return null;
    return countries.find((c) => c.name.toLowerCase() === name) ?? null;
  }, [countries, value.countryIso2, value.countryCode, value.country]);

  const countryIso2 = selectedCountry?.iso2 ?? "";

  /* ------------------------------ Regions ------------------------------ */

  const { data: regionsRes, isLoading: regionsLoading } = useRegions(
    countryIso2 ? { countryIso2, page: 1, limit: LIMIT } : { countryIso2: "" }
  );
  const regions = React.useMemo(() => regionsRes?.items ?? [], [regionsRes]);

  const selectedRegion = React.useMemo(() => {
    const name = String(value.state ?? "").trim().toLowerCase();
    if (!name) return null;
    return regions.find((r) => r.name.toLowerCase() === name) ?? null;
  }, [regions, value.state]);

  /* ------------------------------- Cities ------------------------------ */

  const { data: citiesRes, isLoading: citiesLoading } = useCities(
    countryIso2
      ? {
          countryIso2,
          page: 1,
          limit: LIMIT,
          regionId: selectedRegion ? selectedRegion.id : undefined,
        }
      : { countryIso2: "" }
  );
  const cities = React.useMemo(() => citiesRes?.items ?? [], [citiesRes]);

  /* ------------------------------ Options ------------------------------ */

  const countryOptions: Opt[] = React.useMemo(
    () => countries.map((c) => ({ value: c.iso2, label: c.name })),
    [countries]
  );

  const regionOptions: Opt[] = React.useMemo(
    () => regions.map((r) => ({ value: String(r.id), label: r.name })),
    [regions]
  );

  const cityOptions: Opt[] = React.useMemo(
    () => cities.map((c) => ({ value: String(c.id), label: c.name })),
    [cities]
  );

  const cityValue =
    value.city && cityOptions.find((o) => o.label === value.city)
      ? cityOptions.find((o) => o.label === value.city)!
      : value.city
        ? { value: value.city, label: value.city }
        : null;

  /* ----------------------------- Handlers ------------------------------ */

  const onCountry = (opt: SingleValue<Opt>) => {
    // Region and city belong to a country — clear them with it.
    onChange({
      countryIso2: opt?.value,
      countryCode: opt?.value,
      country: opt?.label,
      state: undefined,
      city: undefined,
    });
  };

  const onRegion = (opt: SingleValue<Opt>) => {
    onChange({
      ...value,
      countryIso2: countryIso2 || undefined,
      countryCode: countryIso2 || undefined,
      country: selectedCountry?.name ?? value.country,
      state: opt?.label,
      city: undefined,
    });
  };

  const onCity = (opt: SingleValue<Opt>) => {
    const city = cities.find((c) => String(c.id) === opt?.value);

    // Picking a city fills in its region, so the stored address is complete
    // even when the region select was skipped.
    const regionName = city?.regionId
      ? regions.find((r) => r.id === city.regionId)?.name
      : undefined;

    onChange({
      ...value,
      countryIso2: countryIso2 || undefined,
      countryCode: countryIso2 || undefined,
      country: selectedCountry?.name ?? value.country,
      state: regionName ?? value.state,
      city: opt?.label,
    });
  };

  const cityPlaceholder = !countryIso2
    ? "Select country first"
    : citiesLoading
      ? "Loading cities…"
      : cities.length === 0
        ? "No cities for this country"
        : "Select city";

  return (
    <div
      className={`grid grid-cols-1 gap-3 ${
        showRegion ? "sm:grid-cols-3" : "sm:grid-cols-2"
      }`}
    >
      <Field label="Country">
        <SelectRS
          instanceId={`${instancePrefix}-country`}
          classNamePrefix="rs"
          styles={selectStyles}
          options={countryOptions}
          value={
            selectedCountry
              ? { value: selectedCountry.iso2, label: selectedCountry.name }
              : null
          }
          isLoading={countriesLoading}
          placeholder="Select country"
          onChange={onCountry}
          isClearable
        />
      </Field>

      {showRegion && (
        <Field label="Region / State">
          <SelectRS
            instanceId={`${instancePrefix}-region`}
            classNamePrefix="rs"
            styles={selectStyles}
            options={regionOptions}
            value={
              selectedRegion
                ? { value: String(selectedRegion.id), label: selectedRegion.name }
                : value.state
                  ? { value: value.state, label: value.state }
                  : null
            }
            isLoading={Boolean(countryIso2) && regionsLoading}
            isDisabled={!countryIso2}
            placeholder={
              !countryIso2
                ? "Select country first"
                : regions.length === 0
                  ? "No regions for this country"
                  : "Select region"
            }
            onChange={onRegion}
            isClearable
          />
        </Field>
      )}

      <Field label="City">
        <SelectRS
          instanceId={`${instancePrefix}-city`}
          classNamePrefix="rs"
          styles={selectStyles}
          options={cityOptions}
          value={cityValue}
          isLoading={Boolean(countryIso2) && citiesLoading}
          isDisabled={!countryIso2}
          placeholder={cityPlaceholder}
          onChange={onCity}
          isClearable
        />
      </Field>
    </div>
  );
}
